/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  WishlistOption,
  Badge,
  TvAssetUtils,
  RelativeTime,
  TimeUtils,
  WatchOption,
  RecordingOption,
  RecordingOptionType,
  RecordingStatus,
  RecordingsUtils,
  AssetOption,
  OptionUtils,
  Asset,
  Series,
  Vod,
  Program,
  ContentTagsUtils,
  isEntitlement,
  AssetUtils,
  Icons,
  TvAsset,
  Recording,
  FolderType,
  PurchaseOption,
  PurchaseOptionType,
  BadgeType,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import AssetUiUtils from 'core/services/utils/AssetUiUtils';
import BadgeEngine from './BadgeEngine';
import { Selectors as WishlistSelectors } from '../../../store/ducks/wishlist';
import { Selectors as SingleRecordingsSelectors } from '../../../store/ducks/recordings/singleRecordings';
import { Selectors as PISelectors } from '../../../../store/ducks/programInfo/data';
import { PiContext } from '../../../../store/ducks/programInfo';
import Colors from '../../../../ui/common/Colors';
import translate from '../../../../services/i18n/translate';
import PricesUtils from '../../../../services/utils/pricesUtils';

export default class PIOptionsBadgeEngine extends BadgeEngine {
  private static isAllowedAssetForBadge(asset?: Readonly<Asset>): asset is Series | Vod | Program | Recording {
    return (
      asset !== undefined &&
      (AssetUtils.isSeries(asset) ||
        AssetUtils.isVod(asset) ||
        AssetUtils.isTvAsset(asset) ||
        AssetUtils.isRecording(asset))
    );
  }

  private static getFutureAiring(asset: Asset): string | undefined {
    if (AssetUtils.isTvAsset(asset) && TvAssetUtils.getRelativeTime(asset) === RelativeTime.FUTURE) {
      const airingTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.startDate));
      return translate.formatString(translate['badge.pi.wishlist.airing'], airingTime).toString();
    }
    return undefined;
  }

  private static handleWishlistOption({ asset }: Readonly<WishlistOption>): Badge | undefined {
    if (PIOptionsBadgeEngine.isAllowedAssetForBadge(asset)) {
      const isWishListed = WishlistSelectors.isAssetWishlisted(store().getState(), asset);
      const label = translate[isWishListed ? 'badge.pi.wishlist.addedToWishlist' : 'badge.pi.wishlist.addToWishlist'];
      const airingLabel = this.getFutureAiring(asset);

      return {
        type: isWishListed ? BadgeType.WISHLIST_ON : BadgeType.WISHLIST_OFF,
        label: airingLabel ? [label, airingLabel] : label,
        icon: isWishListed ? Icons.WISHLIST : Icons.DEFAULT,
      };
    }
    return undefined;
  }

  static handleWatchOption(option: Readonly<WatchOption>): Badge | undefined {
    const { asset } = option;
    const assetEntitlement = asset ? PIOptionsBadgeEngine.getAssetEntitled(asset) : undefined;
    if (PIOptionsBadgeEngine.isAllowedAssetForBadge(asset) && assetEntitlement && !OptionUtils.isWatchTrailer(option)) {
      let labels: string | string[] | undefined;
      let expiring: string | undefined;
      const expiresInI18n = translate['badge.pi.play.expiresIn'];

      // Watch/Resume Recording
      if (AssetUtils.isRecording(asset)) {
        const recordedTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.actualStartTime));
        const label = translate.formatString(translate['badge.pi.play.recorded'], recordedTime).toString();
        expiring = PIOptionsBadgeEngine.getExpiringInLabel(asset, expiresInI18n);
        labels = expiring ? [label, expiring] : label;
      }

      if (AssetUtils.isEpg(asset)) {
        const assetRelativeTime = TvAssetUtils.getRelativeTime(asset);

        // Watch Live
        if (assetRelativeTime === RelativeTime.LIVE) {
          labels = translate['badge.pi.play.airingNow'];
        }

        // Watch/Resume Catchup
        if (assetRelativeTime === RelativeTime.PAST && ContentTagsUtils.isPlaybackAllowedForCatchupAsset(asset)) {
          const airedTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.startDate));
          const label = translate.formatString(translate['badge.pi.play.aired'], airedTime).toString();
          expiring = PIOptionsBadgeEngine.getExpiringInLabel(asset, expiresInI18n);
          labels = expiring ? [label, expiring] : label;
        }
      }

      if (AssetUtils.isVod(asset) && isEntitlement(assetEntitlement)) {
        if (PIOptionsBadgeEngine.isEntitlementRented(assetEntitlement)) {
          // Watch/Resume TVOD Rental
          expiring = PIOptionsBadgeEngine.getExpiringInLabel(asset, expiresInI18n);
          labels = expiring;
        } else {
          // Watch/Resume TVOD Purchase
          const purchasedTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(assetEntitlement.purchaseDate));
          labels = translate.formatString(translate['badge.pi.play.purchasedOn'], purchasedTime).toString();
        }
      }

      if (labels) {
        return {
          type: expiring ? BadgeType.EXPIRY_CLOCK : BadgeType.PLAY,
          label: labels,
          icon: expiring ? Icons.CLOCK : Icons.PLAY_CIRCLE,
        };
      }
    }
    return undefined;
  }

  private static handleDeleteSeriesRecordingOption(option: Readonly<RecordingOption>): Badge | undefined {
    const { totalSeasons } = option;
    if (totalSeasons) {
      const transKey =
        totalSeasons === 1
          ? translate['pi.options.recording.deleteSeries.seasonsRecorded.singular']
          : translate['pi.options.recording.deleteSeries.seasonsRecorded'];
      return {
        type: BadgeType.SERIES_RECORDING,
        label: translate.formatString(transKey, totalSeasons).toString(),
        icon: Icons.DEFAULT,
      };
    }
    return undefined;
  }

  private static handleCancelSeriesRecordingOption(asset: Readonly<TvAsset | Recording>): Badge | undefined {
    const [singleRecording, seriesRecording] = PIOptionsBadgeEngine.getSingleRecordingAndSeriesRecording(asset);
    if (seriesRecording) {
      if (singleRecording && RecordingsUtils.getRecordingStatus(singleRecording) === RecordingStatus.ON_GOING) {
        return {
          type: BadgeType.SERIES_RECORDING,
          label: translate['badge.pi.seriesRecording.recordingNow'],
          icon: Icons.RECORDING_SERIES,
          color: Colors.brand,
        };
      }

      const singlesRecordings = SingleRecordingsSelectors.getRecordings(store().getState());
      if (singlesRecordings.length) {
        const seriesRecordings = singlesRecordings.filter(
          (rec) =>
            rec.seriesId === asset.seriesId && RecordingsUtils.getRecordingStatus(rec) === RecordingStatus.SCHEDULED
        );
        const [nextRecording] = RecordingsUtils.sort(seriesRecordings);

        if (nextRecording) {
          const nextEpisodeTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(nextRecording.startDate));
          return {
            type: BadgeType.SERIES_RECORDING,
            label: translate
              .formatString(translate['badge.pi.seriesRecording.nextEpisode'], nextEpisodeTime)
              .toString(),
            icon: Icons.RECORDING_SERIES,
          };
        }
      }
    }

    return undefined;
  }

  private static handleSingleRecordingOption(asset: Readonly<TvAsset | Recording>): Badge | undefined {
    const assetRelativeTime = TvAssetUtils.getRelativeTime(asset);
    if (assetRelativeTime === RelativeTime.LIVE) {
      return {
        type: BadgeType.SINGLE_RECORDING,
        icon: Icons.AIRS_ON,
        label: translate['pi.options.recording.record.airingNow'],
      };
    }

    if (assetRelativeTime === RelativeTime.FUTURE) {
      const airingTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.startDate));
      return {
        type: BadgeType.SINGLE_RECORDING,
        icon: Icons.AIRS_ON,
        label: translate.formatString(translate['pi.options.recording.record.airing'], airingTime).toString(),
      };
    }

    if (assetRelativeTime === RelativeTime.PAST) {
      const airingTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.startDate));
      return {
        type: BadgeType.SINGLE_RECORDING,
        label: translate.formatString(translate['pi.options.recording.record.aired'], airingTime).toString(),
        icon: Icons.DEFAULT,
      };
    }

    return undefined;
  }

  private static handleSeriesRecordingOption(
    asset: Readonly<TvAsset | Recording>,
    ctx: Readonly<PiContext>
  ): Badge | undefined {
    const seasonData = PISelectors.seasonsData(store().getState(), ctx);
    if (seasonData) {
      const { episodesBySeason } = seasonData;
      const entries = Object.entries(episodesBySeason);

      const allEpisodes: TvAsset[] = [];
      for (let i = 0; i < entries.length; i += 1) {
        const [, episodes] = entries[i];
        episodes.forEach((e) => {
          if (AssetUtils.isTvAsset(e) && TvAssetUtils.getRelativeTime(e) === RelativeTime.FUTURE) {
            allEpisodes.push(e);
          }
        });
      }

      const sortedAllEpisodes = TvAssetUtils.sortByClosestDateRelativeToNow(allEpisodes);

      const [nextEpisode] = sortedAllEpisodes;

      if (nextEpisode) {
        const nextEpisodeTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(nextEpisode.startDate));
        return {
          type: BadgeType.SINGLE_RECORDING,
          label: translate
            .formatString(translate['pi.options.recording.recordSeries.nextEpisode'], nextEpisodeTime)
            .toString(),
          icon: Icons.DEFAULT,
        };
      }
    }

    return undefined;
  }

  private static handleCancelSingleRecordingOption(asset: Readonly<TvAsset | Recording>): Badge | undefined {
    const [singleRecording] = PIOptionsBadgeEngine.getSingleRecordingAndSeriesRecording(asset);

    if (singleRecording && RecordingsUtils.getRecordingStatus(singleRecording) === RecordingStatus.ON_GOING) {
      return {
        type: BadgeType.SINGLE_RECORDING,
        label: translate['badge.pi.singleRecording.recordingNow'],
        icon: Icons.RECORDING_SINGLE,
        color: Colors.brand,
      };
    }

    if (singleRecording && RecordingsUtils.getRecordingStatus(singleRecording) === RecordingStatus.SCHEDULED) {
      const airsOnTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(singleRecording.startDate));
      return {
        type: BadgeType.SINGLE_SCHEDULED_RECORDING,
        label: translate.formatString(translate['badge.pi.singleRecording.scheduled'], airsOnTime).toString(),
        icon: Icons.RECORDING_SINGLE_SCHEDULED,
      };
    }

    return undefined;
  }

  private static handleDeleteSingleRecordingOption(asset: Readonly<TvAsset | Recording>): Badge | undefined {
    const recordedTime = PIOptionsBadgeEngine.toTimeFormat(TimeUtils.toEpoch(asset.startDate));
    return {
      type: BadgeType.SERIES_RECORDING,
      label: translate.formatString(translate['pi.options.recording.delete.recorded'], recordedTime).toString(),
      icon: Icons.DEFAULT,
    };
  }

  private static handleDeleteSeasonRecordingOption(option: Readonly<RecordingOption>): Badge | undefined {
    const { totalRecordings } = option;
    if (totalRecordings) {
      const transKey =
        totalRecordings === 1
          ? translate['pi.options.recording.deleteSeason.episodesRecorded.singular']
          : translate['pi.options.recording.deleteSeason.episodesRecorded'];
      return {
        type: BadgeType.SERIES_RECORDING,
        label: translate.formatString(transKey, totalRecordings).toString(),
        icon: Icons.DEFAULT,
      };
    }
    return undefined;
  }

  private static handleToggleProtectionRecordingOption(asset: Readonly<TvAsset | Recording>): Badge | undefined {
    if (AssetUtils.isRecording(asset)) {
      if (asset.isAssetProtected) {
        return {
          type: BadgeType.PROTECTED_RECORDING,
          label: translate['badge.pi.protectedRecording.recordingProtected'],
          icon: Icons.PROTECTED_RECORDING,
        };
      }

      const singlesRecordings = SingleRecordingsSelectors.getRecordings(store().getState());
      const filteredSingleRecordings = singlesRecordings.filter(
        (e) =>
          [RecordingStatus.ON_GOING, RecordingStatus.COMPLETED].includes(RecordingsUtils.getRecordingStatus(e)) &&
          !e.isAssetProtected
      );

      const transKey =
        filteredSingleRecordings.length === 1
          ? translate['badge.pi.protectedRecording.recordingsLeftToProtect.singular']
          : translate['badge.pi.protectedRecording.recordingsLeftToProtect'];
      return {
        type: BadgeType.PROTECTED_RECORDING,
        label: translate.formatString(transKey, filteredSingleRecordings.length).toString(),
        icon: Icons.DEFAULT,
      };
    }
    return undefined;
  }

  private static handleRecordingOption(ctx: Readonly<PiContext>, option: Readonly<RecordingOption>): Badge | undefined {
    const { asset } = option;
    if (
      PIOptionsBadgeEngine.isAllowedAssetForBadge(asset) &&
      (AssetUtils.isTvAsset(asset) || AssetUtils.isRecording(asset))
    ) {
      switch (option.recordingOptionType) {
        case RecordingOptionType.DELETE_SERIES:
          return PIOptionsBadgeEngine.handleDeleteSeriesRecordingOption(option);
        case RecordingOptionType.CANCEL_SERIES:
        case RecordingOptionType.CANCEL_SERIES_SEASONS:
          return PIOptionsBadgeEngine.handleCancelSeriesRecordingOption(asset);
        case RecordingOptionType.RECORD_SINGLE:
          return PIOptionsBadgeEngine.handleSingleRecordingOption(asset);
        case RecordingOptionType.RECORD_SERIES:
        case RecordingOptionType.RECORD_SERIES_SEASON:
          return PIOptionsBadgeEngine.handleSeriesRecordingOption(asset, ctx);
        case RecordingOptionType.CANCEL_SINGLE:
        case RecordingOptionType.CANCEL_EPISODE:
          return PIOptionsBadgeEngine.handleCancelSingleRecordingOption(asset);
        case RecordingOptionType.DELETE_SINGLE:
        case RecordingOptionType.DELETE_EPISODE:
          return PIOptionsBadgeEngine.handleDeleteSingleRecordingOption(asset);
        case RecordingOptionType.DELETE_SERIES_SEASON:
          return PIOptionsBadgeEngine.handleDeleteSeasonRecordingOption(option);
        case RecordingOptionType.TOGGLE_PROTECTION:
          return PIOptionsBadgeEngine.handleToggleProtectionRecordingOption(asset);
        default:
          break;
      }
    }
    return undefined;
  }

  private static handlePurchaseOption(option: Readonly<PurchaseOption>): Badge | undefined {
    let label: string | undefined;
    const { asset } = option;
    if (asset) {
      if (PIOptionsBadgeEngine.isPurchasedBadgeEnabled() && !PIOptionsBadgeEngine.getAssetEntitled(asset)) {
        if (AssetUtils.isVod(asset) && PIOptionsBadgeEngine.isPurchasedBadgeEnabledForTVOD()) {
          const numberOfDays = AssetUiUtils.rentDurationInDays(option.ppvModule.fullLifeCycle.minutes);
          const priceLabel = PricesUtils.formatPrice(option.productPrice.price);
          label =
            option.purchaseOptionType === PurchaseOptionType.BUY
              ? translate['badge.rails.purchased.tvod.availableToPurchase']
              : translate
                  .formatString(translate['badge.days.rental.value'], numberOfDays.toString(), priceLabel)
                  .toString();
        } else if (AssetUtils.isFolder(asset)) {
          if (asset.folderType === FolderType.SUBSCRIPTION && PIOptionsBadgeEngine.isPurchasedBadgeEnabledForSVOD()) {
            label = translate['badge.rails.purchased.subscription.availableToSubscribe'];
          } else if (
            asset.folderType === FolderType.BUNDLE &&
            PIOptionsBadgeEngine.isPurchasedBadgeEnabledForBoxset()
          ) {
            label =
              option.purchaseOptionType === PurchaseOptionType.BUY
                ? translate['badge.rails.purchased.boxset.availableToPurchase']
                : translate['buttons.rental'];
          }
        }
      }

      if (label) {
        return {
          type: BadgeType.PURCHASE,
          icon: PIOptionsBadgeEngine.getCurrencyIcon(),
          label,
        };
      }
    }
    return undefined;
  }

  private static getOptionBadging(ctx: Readonly<PiContext>, option: Readonly<AssetOption>): Badge | undefined {
    if (OptionUtils.isWishlist(option)) {
      return PIOptionsBadgeEngine.handleWishlistOption(option);
    }

    if (OptionUtils.isWatch(option)) {
      return PIOptionsBadgeEngine.handleWatchOption(option);
    }

    if (OptionUtils.isRecording(option)) {
      return PIOptionsBadgeEngine.handleRecordingOption(ctx, option);
    }

    if (OptionUtils.isPurchaseOption(option)) {
      return PIOptionsBadgeEngine.handlePurchaseOption(option);
    }

    return undefined;
  }

  private static getUIBadging(ctx: Readonly<PiContext>, option: Readonly<AssetOption>): Badge | undefined {
    if (OptionUtils.isMultiOption(option)) {
      let badging: Badge | undefined;

      for (let i = 0; i < option.children.length; i += 1) {
        badging = PIOptionsBadgeEngine.getOptionBadging(ctx, option.children[i]);

        if (badging) {
          return badging;
        }
      }
    }

    return PIOptionsBadgeEngine.getOptionBadging(ctx, option);
  }

  static UI = {
    getBadging: PIOptionsBadgeEngine.getUIBadging,
  };
}
